/** @jsx jsx */
import { jsx } from '@emotion/react';

import { DescriptionPage_CourseType as CourseType } from '__generated__/graphql-types';

import { useLocation, useParams } from 'js/lib/useRouter';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import ConsumerProjectEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/consumer/ConsumerProjectEnrollButtonNotConfigurable';
import MobileCtaModule from 'bundles/unified-description-page-common/components/modules/mobile-cta/MobileCtaModule';
import FixedPageNavigation from 'bundles/unified-description-page-common/components/modules/page-navigation/FixedPageNavigation';
import NavPartnerLogo from 'bundles/unified-description-page-common/components/modules/page-navigation/NavPartnerLogo';
import ProjectPageSEO from 'bundles/unified-description-page-common/components/seo/ProjectPageSEO';
import CoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/shared/CoBrandingPartnerLogos';
import PAGE_TYPES from 'bundles/unified-description-page-common/constants/pageTypes';
import { useCoursePageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import useLogRecentlyViewed from 'bundles/unified-description-page-common/hooks/useRecentlyViewed';
import Renderer from 'bundles/unified-description-page-common/pages/consumer/project/ConsumerPortfolioProjectPageRenderer';
import EventingDataProvider from 'bundles/unified-description-page-common/providers/EventingDataProvider';
import LanguageInfoDialogProvider from 'bundles/unified-description-page-common/providers/LanguageInfoDialogProvider';
import PageInfoProvider from 'bundles/unified-description-page-common/providers/PageInfoProvider';
import PageNavigationProvider from 'bundles/unified-description-page-common/providers/PageNavigationProvider';
import StickyPanelProvider from 'bundles/unified-description-page-common/providers/StickyPanelProvider';
import TranslationsNotificationBannerProvider from 'bundles/unified-description-page-common/providers/consumer/TranslationsNotificationBannerProvider';
import { useXdpRecommendations } from 'bundles/unified-description-page-common/providers/specialization/RecommendationsDataProvider';
import { ModuleNames } from 'bundles/unified-description-page-common/types/moduleConfig';
import {
  PROJECT_NAV_ITEMS,
  getProjectPageNavigationLinks,
} from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getPartnersForCourse from 'bundles/unified-description-page-common/utils/getPartnersForCourse';
import getProductId from 'bundles/unified-description-page-common/utils/getProductId';
import { getConsumerRouteForCourseProducts } from 'bundles/unified-description-page-common/utils/getRouteForCourseProducts';

import _t from 'i18n!nls/xdp';

const ConsumerPortfolioProjectPageRootContent = () => {
  // Projects use same data as for courses.
  const pageData = useCoursePageData();
  const productSlugInUrl = useParams().productSlug;
  const { search } = useLocation();
  const xdpRecommendations = useXdpRecommendations();

  useLogRecentlyViewed({
    name: pageData?.name,
    slug: pageData?.slug,
    partners: pageData?.partners,
    // @ts-expect-error URL: string
    photoUrl: pageData?.photoUrl,
    id: getProductId('course', pageData?.id),
    difficulty: pageData?.difficultyLevel,
    productType: pageData?.courseType,
  });

  if (!pageData || !pageData.courseType) {
    return null;
  } else if (
    !!pageData.courseType &&
    ((pageData.courseType !== CourseType.Project && pageData.courseType !== CourseType.RhymeProject) ||
      productSlugInUrl !== pageData.slug)
  ) {
    redirectClientOrServer(getConsumerRouteForCourseProducts(pageData.slug, pageData.courseType, search));
  }

  const partnersToDisplay = getPartnersForCourse(pageData.partners);

  const pageConfig = {
    modules: [
      { name: ModuleNames.Breadcrumbs },
      { name: ModuleNames.PageBanner },
      {
        name: ModuleNames.ProductOverview,
      },
      { name: ModuleNames.ThinBanner },
      {
        name: ModuleNames.Syllabus,
      },
      {
        name: ModuleNames.Benefits,
      },
      {
        name: ModuleNames.LearnerIncentive,
      },
      {
        name: ModuleNames.Testimonials,
      },
      {
        name: ModuleNames.Upsell,
      },
    ],
  };

  const navigationLinks = getProjectPageNavigationLinks({
    showOutcomes: false,
    pageData,
    pageConfig,
    subscriptionsForwardDiscoveryRecommendations: xdpRecommendations?.recommendationCollectionEntities,
  });
  const productTitle = getProductTitle(pageData.name);

  return (
    <PageInfoProvider
      pageType={PAGE_TYPES.PORTFOLIO_PROJECT}
      navigationIdMap={PROJECT_NAV_ITEMS}
      productSlug={pageData.slug}
    >
      <EventingDataProvider productId={pageData.id} productType="course">
        <PageNavigationProvider navigationLinks={navigationLinks}>
          <StickyPanelProvider>
            <LanguageInfoDialogProvider
              languages={{
                primaryLanguages: pageData.primaryLanguages,
                subtitleLanguages: pageData.subtitleLanguages ?? [],
                translatedLanguages: pageData.translatedLanguages ?? [],
                dubbedLanguages: pageData.dubbedLanguages,
              }}
            >
              <TranslationsNotificationBannerProvider
                translatedLanguages={pageData.translatedLanguages}
                productType={_t('project')}
                // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                originalLanguage={pageData.primaryLanguages[0]}
              >
                <FixedPageNavigation
                  productTitle={productTitle}
                  navigationLinks={navigationLinks}
                  pageCta={
                    <ConsumerProjectEnrollButtonNotConfigurable
                      pageData={pageData}
                      size="small"
                      onlyShowButton
                      productDisplayName={_t('Project')}
                    />
                  }
                  partnerLogo={
                    partnersToDisplay && partnersToDisplay.length > 1 ? (
                      <CoBrandingPartnerLogos partners={partnersToDisplay} isNavPlacement={true} />
                    ) : (
                      <NavPartnerLogo
                        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                        imageSrc={partnersToDisplay?.[0].squareLogo}
                        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                        partnerName={partnersToDisplay?.[0].name}
                      />
                    )
                  }
                />
                <Renderer pageData={pageData} pageConfig={pageConfig} />
                <MobileCtaModule
                  pageCta={
                    <ConsumerProjectEnrollButtonNotConfigurable
                      pageData={pageData}
                      size="small"
                      onlyShowButton
                      productDisplayName={_t('Project')}
                    />
                  }
                />
                <ProjectPageSEO pageData={pageData} disableCrawlerIndexing />
              </TranslationsNotificationBannerProvider>
            </LanguageInfoDialogProvider>
          </StickyPanelProvider>
        </PageNavigationProvider>
      </EventingDataProvider>
    </PageInfoProvider>
  );
};

export default ConsumerPortfolioProjectPageRootContent;
